import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePaymentInput,
  CreateVendorInput,
  DuePayment,
  Vendor,
  VendorStats,
} from '@guabi/shared';
import { api, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export const vendorsQueryKey = (wedId: string) => ['vendors', wedId] as const;

export interface VendorsResponse {
  items: Vendor[];
  stats: VendorStats;
  /** Unpaid and dated, overdue first — computed server-side from the same read. */
  due: DuePayment[];
}

export function useVendors(wedId: string) {
  return useQuery({
    queryKey: vendorsQueryKey(wedId),
    queryFn: () => apiGet<VendorsResponse>(`/weddings/${wedId}/vendors`),
  });
}

/** A vendor or a payment changing moves the table and the Home tab together. */
function useVendorMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vendorsQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
    },
  });
}

export function useCreateVendor(wedId: string) {
  return useVendorMutation<CreateVendorInput, Vendor>(
    (input) => apiPost<Vendor>(`/weddings/${wedId}/vendors`, input),
    wedId,
  );
}

export function useUpdateVendor(wedId: string) {
  return useVendorMutation<
    { vendorId: string; input: Partial<CreateVendorInput> & { updatedAt: string } },
    Vendor
  >(
    ({ vendorId, input }) => apiPatch<Vendor>(`/weddings/${wedId}/vendors/${vendorId}`, input),
    wedId,
  );
}

export function useDeleteVendor(wedId: string) {
  return useVendorMutation<{ vendorId: string }>(
    ({ vendorId }) => apiDelete(`/weddings/${wedId}/vendors/${vendorId}`),
    wedId,
  );
}

/**
 * The schedule has its own endpoints, so a PATCH of the vendor can never wipe it
 * by omission. Each of these returns the whole vendor back.
 */
export function useAddPayment(wedId: string) {
  return useVendorMutation<
    { vendorId: string; input: CreatePaymentInput & { updatedAt: string } },
    Vendor
  >(
    ({ vendorId, input }) =>
      apiPost<Vendor>(`/weddings/${wedId}/vendors/${vendorId}/payments`, input),
    wedId,
  );
}

export function useUpdatePayment(wedId: string) {
  return useVendorMutation<
    {
      vendorId: string;
      paymentId: string;
      input: Partial<CreatePaymentInput> & { updatedAt: string };
    },
    Vendor
  >(
    ({ vendorId, paymentId, input }) =>
      apiPatch<Vendor>(`/weddings/${wedId}/vendors/${vendorId}/payments/${paymentId}`, input),
    wedId,
  );
}

export function useDeletePayment(wedId: string) {
  return useVendorMutation<{ vendorId: string; paymentId: string }, Vendor>(
    ({ vendorId, paymentId }) =>
      apiDelete<Vendor>(`/weddings/${wedId}/vendors/${vendorId}/payments/${paymentId}`),
    wedId,
  );
}

export type VendorEmailKind = 'inquiry' | 'negotiate' | 'confirm';

/**
 * A draft email to a supplier. Returns text and sends nothing — Guabi has no
 * way to email a supplier and should not acquire one; the couple sends it from
 * their own address, which is also where the reply needs to land.
 */
export function useVendorEmailDraft(wedId: string) {
  const qc = useQueryClient();
  return useMutation<
    { text: string },
    Error,
    { vendorId: string; kind: VendorEmailKind; note?: string }
  >({
    mutationFn: (body) =>
      api<{ text: string }>(`/weddings/${wedId}/ai/vendor-email`, {
        method: 'POST',
        body: JSON.stringify({ ...body, locale: navigator.language }),
        timeoutMs: 28_000,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] }),
  });
}
